/**
 * Visual Help System Provider
 *
 * Central context provider for the comprehensive help system.
 * Manages state for:
 * - Resource Center panel
 * - Keyboard Shortcuts modal
 * - Feature Tours
 * - Onboarding checklist
 * - Proactive help nudges
 *
 * Global keyboard shortcuts:
 * - F1: Open Resource Center
 * - Ctrl+/: Open Keyboard Shortcuts
 * - ?: Open Keyboard Shortcuts (when not in input)
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const VisualHelpContext = createContext(null);

// Spotlight duration in ms
const SPOTLIGHT_DURATION = 3000;

export function useVisualHelp() {
  const context = useContext(VisualHelpContext);
  if (!context) {
    throw new Error('useVisualHelp must be used within a VisualHelpProvider');
  }
  return context;
}

export function VisualHelpProvider({ children }) {
  // Panel states
  const [isResourceCenterOpen, setResourceCenterOpen] = useState(false);
  const [isShortcutsOpen, setShortcutsOpen] = useState(false);

  // Tour state
  const [activeTour, setActiveTour] = useState(null);

  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);

  // Current context (for contextual help)
  // Uses a simple state that can be updated externally
  const [currentView, setCurrentView] = useState('dashboard');

  // Spotlight state - which element to highlight
  const [spotlightTarget, setSpotlightTarget] = useState(null);
  const spotlightTimeoutRef = useRef(null);

  // Navigation callback - registered by AppContent
  const navigateCallbackRef = useRef(null);

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // F1 -> Resource Center
      if (e.key === 'F1') {
        e.preventDefault();
        setResourceCenterOpen(true);
        return;
      }

      // Ctrl+/ -> Keyboard Shortcuts
      if (e.ctrlKey && e.key === '/') {
        e.preventDefault();
        setShortcutsOpen(true);
        return;
      }

      // ? alone (not in input) -> Keyboard Shortcuts
      if (e.key === '?' && !['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) {
        if (!e.target.isContentEditable) {
          e.preventDefault();
          setShortcutsOpen(true);
        }
      }

      // Escape -> Close any open panel
      if (e.key === 'Escape') {
        if (isResourceCenterOpen) {
          setResourceCenterOpen(false);
        } else if (isShortcutsOpen) {
          setShortcutsOpen(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isResourceCenterOpen, isShortcutsOpen]);

  // Actions
  const openResourceCenter = useCallback(() => {
    setResourceCenterOpen(true);
    setShortcutsOpen(false);
  }, []);

  const closeResourceCenter = useCallback(() => {
    setResourceCenterOpen(false);
  }, []);

  const openShortcuts = useCallback(() => {
    setShortcutsOpen(true);
    setResourceCenterOpen(false);
  }, []);

  const closeShortcuts = useCallback(() => {
    setShortcutsOpen(false);
  }, []);

  const startTour = useCallback((tourId = 'main') => {
    setActiveTour(tourId);
    setResourceCenterOpen(false);
    setShortcutsOpen(false);
  }, []);

  const endTour = useCallback(() => {
    setActiveTour(null);
  }, []);

  const dismissOnboarding = useCallback(() => {
    setOnboardingDismissed(true);
    setShowOnboarding(false);
  }, []);

  // Update current view (call from navigation handlers)
  const updateCurrentView = useCallback((view) => {
    setCurrentView(view);
  }, []);

  // Spotlight an element (for "Go there" navigation)
  const spotlight = useCallback((targetId) => {
    // Clear any existing timeout
    if (spotlightTimeoutRef.current) {
      clearTimeout(spotlightTimeoutRef.current);
    }

    // Set the spotlight target
    setSpotlightTarget(targetId);

    // Auto-clear after duration
    spotlightTimeoutRef.current = setTimeout(() => {
      setSpotlightTarget(null);
    }, SPOTLIGHT_DURATION);
  }, []);

  // Clear spotlight
  const clearSpotlight = useCallback(() => {
    if (spotlightTimeoutRef.current) {
      clearTimeout(spotlightTimeoutRef.current);
    }
    setSpotlightTarget(null);
  }, []);

  // Register navigation callback (called by AppContent)
  const registerNavigate = useCallback((callback) => {
    navigateCallbackRef.current = callback;
  }, []);

  // Navigate to a view with spotlight effect (used by FloatingHelpPanel)
  // NOTE: Does NOT close the help panel - it's non-blocking by design
  const navigateTo = useCallback((viewId) => {
    // First, spotlight the sidebar item
    spotlight(`sidebar-${viewId}`);

    // Navigate immediately (help panel stays open - non-blocking!)
    if (navigateCallbackRef.current) {
      navigateCallbackRef.current(viewId);
    }
  }, [spotlight]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (spotlightTimeoutRef.current) {
        clearTimeout(spotlightTimeoutRef.current);
      }
    };
  }, []);

  // CRITICAL FIX: Memoize value to prevent infinite re-renders
  // Without useMemo, a new object is created on every render, causing all consumers to re-render
  const value = React.useMemo(() => ({
    // State
    isResourceCenterOpen,
    isShortcutsOpen,
    activeTour,
    showOnboarding: showOnboarding && !onboardingDismissed,
    currentView,
    spotlightTarget,

    // Actions
    openResourceCenter,
    closeResourceCenter,
    openShortcuts,
    closeShortcuts,
    startTour,
    endTour,
    setShowOnboarding,
    dismissOnboarding,
    updateCurrentView,
    spotlight,
    clearSpotlight,
    registerNavigate,
    navigateTo,
  }), [
    isResourceCenterOpen,
    isShortcutsOpen,
    activeTour,
    showOnboarding,
    onboardingDismissed,
    currentView,
    spotlightTarget,
    openResourceCenter,
    closeResourceCenter,
    openShortcuts,
    closeShortcuts,
    startTour,
    endTour,
    setShowOnboarding,
    dismissOnboarding,
    updateCurrentView,
    spotlight,
    clearSpotlight,
    registerNavigate,
    navigateTo,
  ]);

  return <VisualHelpContext.Provider value={value}>{children}</VisualHelpContext.Provider>;
}

export default VisualHelpProvider;
